import { CARD_TYPE, errorMessage, finalExp, isValidPesel } from 'utils/validation'
import ConsentPreviewPopup from 'components/popups/ConsentPreview'
import InputWithOptions from '../Contract/InputWithOptions'
import { selectForm, updateValue } from '../formSlice'
import { useDispatch, useSelector } from 'react-redux'
import useTranslation from 'hooks/useTranslation'
import Checkbox from 'components/inputs/Checkbox'
import { checkIsSmallScreen } from 'utils/screen'
import usePopupState from 'hooks/usePopupState'
import Select from 'components/inputs/Select'
import Submit from 'components/inputs/Submit'
import Input from 'components/inputs/Input'
import { preregister } from 'rest/urls'
import { POST } from 'rest/request'
import { useState } from 'react'

const Form = ({ block, submit, showWarning, showConsentPopup }) => {
  const { firstName = '', lastName = '', mail = '', phone = '', rulesAgreement = false, city = '', postalCode = '', street = '', houseNumber = '', localNumber = '', pesel = '', streetPrefix, cityDistricted, cardType = CARD_TYPE.idCard, identityCard = '' } = useSelector(selectForm)
  const [needOtp, setOtp] = useState(true)
  const [passwords, setPasswords] = useState({
    first: '',
    second: '',
  })
  const [warning, setWarning] = useState({
    firstName: false,
    lastName: false,
    mail: false,
    phone: false,
    password: false,
    passwordSec: false,
    rulesAgreement: false,
    city: false,
    postalCode: false,
    street: false,
    houseNumber: false,
    pesel: false,
    identityCard: false,
  })
  const refs = []

  const dispatch = useDispatch()
  const { t } = useTranslation('form')
  const { popup, openPopup } = usePopupState()

  const consentArray = t('IAcceptTheTermsOfServiceAndConfirmThatIHaveReadThePrivacyPolicyArray')
  const changeHandler = e => {
    if (!e) return
    const { name, value, checked, type } = e.target
    const newValue = type === 'checkbox' ? checked : value
    if (name.includes('password')) {
      setPasswords({
        ...passwords,
        [name === 'password' ? 'first' : 'second']: value
      })
    } else {
      dispatch(updateValue({ name, value: newValue }))
    }


    if (name === 'cardType' && identityCard) dispatch(updateValue({ name: 'identityCard', value: '' }))
    if (newValue && warning[name] && (
      (name === 'houseNumber') ||
      (name === 'rulesAgreement') ||
      (name === 'firstName' && newValue.length > 2) ||
      (name === 'lastName' && newValue.length > 1) ||
      (name === 'mail' && newValue.match(finalExp.mail)) ||
      (name === 'phone' && newValue.match(finalExp.phone)) ||
      (name === 'password' && newValue.match(finalExp.password)) ||
      (name === 'passwordSec' && newValue === passwords.first) ||
      ((name === 'city' || name === 'street') && value.length > 1) ||
      (name === 'postalCode' && value.length === 6) ||
      (name === 'pesel' && isValidPesel(value)) ||
      (name === 'identityCard' && value.match(finalExp[cardType]))))
      setWarning({
        ...warning,
        [name]: false,
      })
  }

  const blurHandler = e => {
    if (!e) return
    const { name, value } = e.target
    let warn = false

    switch (name) {
      case 'firstName':
        warn = value.length < 3 ? errorMessage(t, 3 - value.length) : false
        break

      case 'lastName':
        warn = value.length < 2 ? errorMessage(t, 2 - value.length) : false
        break

      case 'mail':
        if (value.match(finalExp.mail)) checkFormData(name, { email: value })
        else warn = t('wrongEmail')
        break

      case 'phone':
        if (value.length < 9) warn = errorMessage(t, 9 - value.length)
        else if (!value.match(finalExp.phone)) warn = t('wrongNumber')
        else checkFormData(name, { phone: value })
        break

      case 'password':
        warn = passwordMessage(value)
        break

      case 'passwordSec':
        warn = passwords.first !== passwords.second ? t('passwordsDoNotMatch') : false
        break

      case 'houseNumber':
        warn = !value ? t('wrongNumber') : false
        break

      case 'postalCode':
        warn = !value.match(finalExp.postalCode) ? '**-***' : false
        break

      case 'pesel':
        warn = value.length < 11 ? errorMessage(t, 11 - value.length) : !isValidPesel(value) ? t('incorrectPESEL') : false
        break

      case 'identityCard':
        if ((cardType === CARD_TYPE.idCard || cardType === CARD_TYPE.passport) && value.length < 9) warn = errorMessage(t, 9 - value.length)
        else if (cardType === CARD_TYPE.drivingLicense && value.length < 12) warn = errorMessage(t, 12 - value.length)
        else if (!value.match(finalExp[cardType])) warn = t('wrongNumber')
        break

      case 'city':
      case 'street':
        warn = value.length < 2 ? errorMessage(t, 2 - value.length) : false
        break

      default: warn = false
    }

    if (warn !== warning[name]) setWarning({
      ...warning,
      [name]: warn,
    })
  }

  const checkFormData = async (name, body) => {
    try {
      const response = await POST(preregister, { body })
      const { valid = true, need_phone_otp } = response
      if (need_phone_otp !== needOtp) setOtp(need_phone_otp)
      if (!valid) {
        document.activeElement.blur()
        setWarning({
          ...warning,
          [name]: t('alreadyInUse'),
        })
        showWarning(name === 'mail')
      }

    } catch (err) {
      console.warn('Fetch preregister failed')
    }
  }

  const passwordMessage = (p = '') => {
    if (p.length < 8) return errorMessage(t, 8 - p.length)
    else if (p.match(finalExp.password)) return false

    let warn = ''
    if (!p.match(/^(?=.*?[A-Z]).*$/)) warn += t('lowercaseLetter')
    if (!p.match(/^(?=.*?[a-z]).*$/)) warn += (warn ? ', ' : '') + t('uppercaseLetter')
    if (!p.match(/^(?=.*?[0-9]).*$/)) warn += (warn ? ', ' : '') + t('number')
    if (!p.match(/^(?=.*?[#?!@$%^&*-]).*$/)) warn += (warn ? ', ' : '') + t('symbol')
    return `${t('missing')}: ${warn}`
  }

  const enterClicked = ref => {
    const index = refs.findIndex(r => Object.is(r, ref))
    if (index === -1) return
    if (refs[index + 1]) refs[index + 1].focus()
    else if (index + 1 === refs.length && !block) validation()
  }

  const validation = () => {
    const peselOK = isValidPesel(pesel)
    const idOK = !!identityCard.match(finalExp[cardType])
    const codeOK = !!postalCode.match(finalExp.postalCode)
    const registrationFormValid = rulesAgreement && firstName.length > 2 && lastName.length > 1 && mail.match(finalExp.mail) && phone.match(finalExp.phone) && passwords.first.length > 7 && passwords.second === passwords.first
    const contractFormValid = city.length > 1 && codeOK && street.length > 1 && houseNumber && peselOK && idOK
    const validateOnlyRegistrationForm = checkIsSmallScreen()

    if (validateOnlyRegistrationForm ? (registrationFormValid && contractFormValid) : registrationFormValid) {
      const body = {
        first_name: firstName,
        surname: lastName,
        email: mail, phone,
        password: passwords.first,
        password_confirmation: passwords.second,
        terms: rulesAgreement
      }
      if (!validateOnlyRegistrationForm) {
        body.city = city
        body.zip_code = postalCode
        body.street = street
        body.building_nr = houseNumber
        body.apartment_nr = localNumber
        body.pesel = pesel
        body.identity_card_type = cardType
        body.identity_card_number = identityCard
      }
      submit(needOtp, body)

    } else {
      const newErrors = {
        firstName: firstName.length < 3 ? errorMessage(t, 3 - firstName.length) : false,
        lastName: lastName.length < 2 ? errorMessage(t, 2 - lastName.length) : false,
        mail: !mail.match(finalExp.mail) ? t('wrongEmail') : false,
        phone: phone.length < 9 ? errorMessage(t, 9 - phone.length) : !phone.match(finalExp.phone) ? t('wrongNumber') : false,
        password: passwordMessage(passwords.first),
        passwordSec: passwords.first !== passwords.second ? t('passwordsDoNotMatch') : false,
        rulesAgreement: !rulesAgreement,
      }

      if (!validateOnlyRegistrationForm) {
        newErrors.city = city.length < 2 ? errorMessage(t, 2 - city.length) : false
        newErrors.postalCode = !codeOK ? '**-***' : false
        newErrors.street = street.length < 2 ? errorMessage(t, 2 - street.length) : false
        newErrors.houseNumber = !houseNumber ? t('wrongNumber') : false
        newErrors.pesel = pesel.length < 11 ? errorMessage(t, 11 - pesel.length) : !peselOK ? t('incorrectPESEL') : false
        newErrors.identityCard = !idOK ? t('wrongNumber') : false
      }

      setWarning(newErrors)
    }
  }


  // eslint-disable-next-line jsx-a11y/anchor-is-valid
  const Link = ({ type, children }) => <a
    className='--link'
    onClick={e => {
      e.preventDefault()
      e.stopPropagation()
      if (showConsentPopup) showConsentPopup(type)
      else openPopup({ type: 'consent', content: type })
    }}>
    {children}
  </a>

  return (
    <>
      {popup?.type === 'consent' && <ConsentPreviewPopup
        type={popup.content}
      />}
      <div className='registration__form form'>
        <p className='registration__form__info'>
          {t(`toRentAWarehouseOnlineYouNeedToLogInThisWayYouWillBeAbleToUseTheFullCapabilitiesOfOurSystems.${process.env.REACT_APP_TYPE}`)}
        </p>

        <div className='registration__form__container'>
          <div className='registration__form__left'>
            <Input
              required
              ref={r => refs.push(r)}
              value={firstName}
              name='firstName'
              label={t('firstName')}
              minLength='3'
              maxLength='100'
              disabled={block}
              warning={warning.firstName}
              placeholder={t('enterFirstName')}
              blurHandler={blurHandler}
              enterClicked={enterClicked}
              changeHandler={changeHandler} />

            <Input
              required
              ref={r => refs.push(r)}
              value={lastName}
              name='lastName'
              label={t('lastName')}
              minLength='2'
              maxLength='100'
              disabled={block}
              warning={warning.lastName}
              placeholder={t('enterLastName')}
              blurHandler={blurHandler}
              enterClicked={enterClicked}
              changeHandler={changeHandler} />

            <Input
              required
              ref={r => refs.push(r)}
              value={mail}
              name='mail'
              label={t('email')}
              minLength='4'
              maxLength='100'
              disabled={block}
              warning={warning.mail}
              placeholder={t('enterEmail')}
              blurHandler={blurHandler}
              enterClicked={enterClicked}
              changeHandler={changeHandler} />

            <Input
              required
              ref={r => refs.push(r)}
              value={phone}
              name='phone'
              type='tel'
              label={t('phoneNumber')}
              minLength='9'
              maxLength='12'
              disabled={block}
              warning={warning.phone}
              placeholder={t('enterPhoneNumber')}
              blurHandler={blurHandler}
              enterClicked={enterClicked}
              changeHandler={changeHandler} />

            <Input
              required
              ref={r => refs.push(r)}
              value={passwords.first}
              name='password'
              type='password'
              label={t('password')}
              minLength='8'
              maxLength='60'
              disabled={block}
              warning={warning.password}
              placeholder={t('enterPassword')}
              blurHandler={blurHandler}
              enterClicked={enterClicked}
              changeHandler={changeHandler} />

            <Input
              required
              ref={r => refs.push(r)}
              value={passwords.second}
              name='passwordSec'
              type='password'
              label={t('repeatPassword')}
              minLength='8'
              maxLength='60'
              disabled={block}
              warning={warning.passwordSec}
              placeholder={t('reEnterPassword')}
              blurHandler={blurHandler}
              enterClicked={enterClicked}
              changeHandler={changeHandler} />

            <Input
              required
              ref={r => refs.push(r)}
              value={pesel}
              name='pesel'
              type='password'
              className='--onlyDesktop'
              label={t('pesel')}
              minLength='11'
              maxLength='11'
              disabled={block}
              warning={warning.pesel}
              placeholder={t('enterPESEL')}
              enterClicked={enterClicked}
              changeHandler={changeHandler}
              blurHandler={blurHandler} />
          </div>

          <div className='registration__form__right'>
            <InputWithOptions
              required
              ref={r => refs.push(r)}
              value={city}
              name='city'
              label={t('city')}
              minLength='2'
              maxLength='200'
              disabled={block}
              warning={warning.city}
              placeholder={t('enterCity')}
              enterClicked={enterClicked}
              changeHandler={changeHandler}
              blurHandler={blurHandler} />

            <InputWithOptions
              required
              ref={r => refs.push(r)}
              value={street}
              name='street'
              label={t('street')}
              minLength='2'
              maxLength='200'
              disabled={block}
              additionalValues={{ city }}
              warning={warning.street}
              placeholder={t('enterStreet')}
              enterClicked={enterClicked}
              changeHandler={changeHandler}
              blurHandler={blurHandler} />

            <InputWithOptions
              required
              ref={r => refs.push(r)}
              value={houseNumber}
              name='houseNumber'
              label={t('houseNumber')}
              maxLength='10'
              disabled={block}
              additionalValues={city.length > 1 && street.length > 1 ? { city, cityDistricted: cityDistricted || city, street, houseNumber } : false}
              warning={warning.houseNumber}
              placeholder={t('enterHouseNumber')}
              enterClicked={enterClicked}
              changeHandler={changeHandler}
              blurHandler={blurHandler} />

            <Input
              ref={r => refs.push(r)}
              value={localNumber}
              name='localNumber'
              type='tel'
              label={t('apartmentNumber')}
              maxLength='10'
              disabled={block}
              placeholder={t('enterApartmentNumber')}
              enterClicked={enterClicked}
              changeHandler={changeHandler} />

            <InputWithOptions
              required
              ref={r => refs.push(r)}
              value={postalCode}
              name='postalCode'
              label={t('postalCode')}
              maxLength='6'
              disabled={block}
              additionalValues={city.length > 1 && street.length > 1 ? { city, street, houseNumber, streetPrefix } : false}
              warning={warning.postalCode}
              placeholder={t('enterPostalCode')}
              enterClicked={enterClicked}
              changeHandler={e => {
                e.target.value = e.target.value.replace(/^(\d{2})(\d+)$/, '$1-$2')
                changeHandler(e)
              }}
              blurHandler={blurHandler} />

            <Select
              label={t('documentType')}
              value={cardType}
              name='cardType'
              setValue={value => {
                changeHandler({ target: { name: 'cardType', value } })
              }}
              options={Object.values(CARD_TYPE).map((v) => ({
                value: v,
                name: t(v === CARD_TYPE.idCard ? 'idNumber' : v === CARD_TYPE.passport ? 'passportNumber' : 'drivingLicenseNumber')
              }))} />

            <Input
              required
              ref={r => refs.push(r)}
              value={identityCard}
              name='identityCard'
              type='password'
              label={t(cardType === CARD_TYPE.idCard ? 'idNumber' : cardType === CARD_TYPE.passport ? 'passportNumber' : 'drivingLicenseNumber')}
              disabled={block}
              warning={warning.identityCard}
              placeholder={t(cardType === CARD_TYPE.idCard ? 'enterIdNumber' : cardType === CARD_TYPE.passport ? 'enterPassportNumber' : 'enterDrivingLicenseNumber')}
              enterClicked={enterClicked}
              changeHandler={changeHandler}
              blurHandler={blurHandler} />
          </div>
        </div>

        <div className={`checkbox__area input__default${warning.rulesAgreement ? ' --warning' : ''}`}>
          <label className='input__label'>
            <span>{t('termsAndConditions')}</span>
          </label>

          <Checkbox
            required
            disabled={block}
            name='rulesAgreement'
            checked={rulesAgreement}
            label={<>{consentArray[0]} {<Link type='terms'>{consentArray[1]}</Link>} {consentArray[2]} {<Link type='privacy'>{consentArray[3]}</Link>}</>}
            changeHandler={changeHandler} />
        </div>

        <Submit
          text={t('signUp')}
          action={() => !block && validation()} />
      </div>
    </>
  )
}

export default Form